'use client'

import { useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  useAccessToken,
  useAccessTokenRoles,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import {
  getQuotesClient,
  getSelectedQuoteQuery,
  locationEligibilityQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import { LocationAttribute } from '@b-stock/location-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'
import { COMPARE_LIMIT } from '@/components/listings/compare/constants'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

/** `unavailable` is settled-with-nothing, distinct from still-resolving. */
export type CompareTlcStatus = 'pending' | 'ready' | 'unavailable'

/** Quote-derived Logistics figures for one compared listing, both in dollars. */
export type CompareTlc = {
  shippingFee: number | null
  totalLandedCost: number | null
  status: CompareTlcStatus
}

type Slot = {
  listingId: string | undefined
  shippingFee: number | null
  totalLandedCost: number | null
  /** A selected quote exists, so the figures above are real. */
  hasQuote: boolean
  /** Settled with no quote and the destination is eligible: generate one. */
  needsQuote: boolean
  /** The type this buyer/destination is eligible for, not merely allowed. */
  shipmentType: ShipmentType | undefined
  /** Some input to the derivation is still resolving. */
  isPending: boolean
}

/** Exactly COMPARE_LIMIT of T, so raising the cap fails to compile. */
type SlotTuple<T, R extends T[] = []> = R['length'] extends typeof COMPARE_LIMIT
  ? readonly [...R]
  : SlotTuple<T, [...R, T]>

const EMPTY_SLOT: Omit<Slot, 'listingId'> = {
  shippingFee: null,
  totalLandedCost: null,
  hasQuote: false,
  needsQuote: false,
  shipmentType: undefined,
  isPending: false,
}

/**
 * Quote + seller premium + TLC for one listing, off the search facade so no
 * per-listing details fetch is needed. Mirrors useCostContextAnalyticsProperties.
 */
const useListingTlc = (
  listing: CombinedListingSearchResult | undefined,
  accountId: string | undefined,
  defaultLocationId: string | undefined,
  enabled: boolean,
  /** Only an active buyer has SHIPMENT_QUOTE_READ; others 403 on read. */
  isActiveBuyer: boolean
): Slot => {
  const listingId = listing?.listingId
  const active = enabled && !!listingId && !!accountId && isActiveBuyer

  // Same cache key the LDP and ManifestSummaryPopup seed, so repeats are free.
  const quoteQuery = getSelectedQuoteQuery.useWithOpts(
    [listingId ?? '', accountId],
    { enabled: active }
  )

  // Keyed by SELLER account, so repeated sellers collapse to one request.
  const sellerId = listing?.sellerId
  const subscriptionQuery = activeSubscriptionsQuery.useQuery(
    active ? sellerId : undefined
  )

  const selectedQuote = quoteQuery.data

  const quoteNeedsGeneration = active && quoteQuery.isFetched && !selectedQuote

  // Eligibility, not the listing's allowedShipmentTypes: the LDP quotes on
  // eligibleShipmentTypes[0], and anything else lands a different fee.
  const eligibilityQuery = locationEligibilityQuery.useWithOpts(
    [{ listingId: listingId ?? '', locationId: [defaultLocationId ?? ''] }],
    { enabled: quoteNeedsGeneration && !!defaultLocationId }
  )
  const eligibility = eligibilityQuery.data?.[0]

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium: subscriptionQuery.data?.buyersPremium ?? 0,
    currentBidDollars: listing?.currentPrice ?? 0,
    selectedQuote,
  })

  const tlcTotals = useTlcTotals({
    additionalCharges,
    currency: listing?.currency,
    listingId: listingId ?? '',
    listingType: listing?.listingType,
    // useTlcTotals wants cents; the facade exposes dollars.
    msrpPrice: listing?.retailPrice == null ? null : listing.retailPrice * 100,
    selectedQuote,
    units: listing?.units ?? 0,
  })

  if (!active) return { ...EMPTY_SLOT, listingId }

  // `active` implies a listing: it is derived from `listing?.listingId`.
  const currentPrice = listing.currentPrice

  return {
    listingId,
    shippingFee: selectedQuote ? tlcTotals.shippingCents / 100 : null,
    // A withheld price has nothing to land a cost on.
    totalLandedCost:
      selectedQuote && currentPrice > 0
        ? tlcTotals.totalLandedCostWithTax
        : null,
    hasQuote: !!selectedQuote,
    needsQuote:
      quoteNeedsGeneration &&
      !!defaultLocationId &&
      eligibilityQuery.isFetched &&
      (eligibility?.isEligible ?? false),
    shipmentType: eligibility?.eligibleShipmentTypes[0] as
      | ShipmentType
      | undefined,
    isPending:
      !quoteQuery.isFetched ||
      quoteQuery.isFetching ||
      subscriptionQuery.isLoading ||
      (quoteNeedsGeneration &&
        !!defaultLocationId &&
        !eligibilityQuery.isFetched),
  }
}

/**
 * Shipping fee and Total landed cost for the compared listings, from the same
 * quote chain the LDP uses — the search index cannot carry a per-buyer cost.
 * Eligible listings without a quote get one from the idempotent batch endpoint.
 *
 * A listing is attempted at most once per page load, so a request that fails
 * (a connectivity blip, say) leaves that column unavailable until reload —
 * closing and reopening the panel does not retry, since the hook stays mounted
 * with the panel and keeps its attempted set. Deliberate: the alternative is
 * re-quoting on every open.
 */
export const useCompareShippingQuotes = (
  selectedListings: CombinedListingSearchResult[],
  /** Panel open. The panel stays mounted, so without this a tick would quote. */
  enabled: boolean
): { byListingId: Map<string, CompareTlc> } => {
  const accessToken = useAccessToken()
  const accountId = useDecodedAccessToken()?.accountId ?? undefined
  const isActiveBuyer = useAccessTokenRoles()?.buyer === RoleStatus.Active
  const queryClient = useQueryClient()

  const locationsQuery = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        accountId: accountId ? [accountId] : [],
      },
    ],
    { enabled: enabled && !!accountId }
  )
  const defaultLocation = locationsQuery.data?.locations[0]
  const isLocationPending = enabled && !!accountId && !locationsQuery.isFetched

  // Fixed slot count: the rules of hooks forbid deriving it from the selection.
  const columns = selectedListings.slice(0, COMPARE_LIMIT)
  const slots: SlotTuple<Slot> = [
    useListingTlc(
      columns[0],
      accountId,
      defaultLocation?.id,
      enabled,
      isActiveBuyer
    ),
    useListingTlc(
      columns[1],
      accountId,
      defaultLocation?.id,
      enabled,
      isActiveBuyer
    ),
    useListingTlc(
      columns[2],
      accountId,
      defaultLocation?.id,
      enabled,
      isActiveBuyer
    ),
    useListingTlc(
      columns[3],
      accountId,
      defaultLocation?.id,
      enabled,
      isActiveBuyer
    ),
  ]

  // Per-id, not a joined key: successes leaving the set would re-arm failures.
  const attemptedIdsRef = useRef<Set<string>>(new Set())
  const [inFlightIds, setInFlightIds] = useState<ReadonlySet<string>>(new Set())

  const requestItems = slots.flatMap((s) =>
    s.needsQuote && s.listingId && !attemptedIdsRef.current.has(s.listingId)
      ? [{ listingId: s.listingId, shipmentType: s.shipmentType }]
      : []
  )
  const requestKey = requestItems.map((i) => i.listingId).join(',')
  const requestItemsRef = useRef(requestItems)
  requestItemsRef.current = requestItems

  useEffect(() => {
    if (!enabled || !requestKey || !defaultLocation || !accountId) return
    const items = requestItemsRef.current
    const ids = items.map((i) => i.listingId)
    ids.forEach((id) => attemptedIdsRef.current.add(id))
    setInFlightIds((prev) => new Set([...prev, ...ids]))

    const run = async () => {
      try {
        const { data: results } = await getQuotesClient().handleBatchQuotes(
          {
            createBatchQuotesDto: {
              items: items.map((i) => {
                // Eligible with no eligible types is unexpected; the LDP
                // defaults to Binding too, so log rather than diverge.
                if (!i.shipmentType)
                  console.warn(
                    '[useCompareShippingQuotes] no eligible shipment type; defaulting to Binding',
                    i.listingId
                  )
                return {
                  listingId: i.listingId,
                  destinationLocationId: defaultLocation.id,
                  shipmentType: i.shipmentType ?? ShipmentType.Binding,
                  accountId,
                }
              }),
            },
          },
          buildAxiosRequestConfig({ accessToken })
        )
        // The batch response carries no amount, so refetch what now has one.
        await Promise.all(
          results
            .filter((r) => r.success)
            .map((r) =>
              queryClient.invalidateQueries({
                queryKey: getSelectedQuoteQuery.getKey(accessToken, [
                  r.listingId,
                  accountId,
                ]),
              })
            )
        )
        const failed = results.filter((r) => !r.success)
        if (failed.length > 0) {
          // Documented per-item contract, not an outage — nothing to retry.
          console.warn(
            '[useCompareShippingQuotes] batch could not quote',
            failed.map((r) => ({ listingId: r.listingId, error: r.error }))
          )
        }
      } catch (error) {
        // Supplementary — the affected columns just report unavailable.
        console.warn(
          '[useCompareShippingQuotes] handleBatchQuotes failed',
          error
        )
      } finally {
        setInFlightIds((prev) => {
          const next = new Set(prev)
          ids.forEach((id) => next.delete(id))
          return next
        })
      }
    }
    void run()
    // Intentionally narrow: only re-run when the set to request changes.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [enabled, requestKey, defaultLocation?.id, accountId])

  const statusOf = (slot: Slot): CompareTlcStatus => {
    if (slot.hasQuote) return 'ready'
    if (!slot.listingId) return 'unavailable'
    if (isLocationPending || slot.isPending) return 'pending'
    if (inFlightIds.has(slot.listingId)) return 'pending'
    // Queued but the effect has not committed: avoids a one-frame em-dash.
    if (slot.needsQuote && !attemptedIdsRef.current.has(slot.listingId))
      return 'pending'
    return 'unavailable'
  }

  const byListingId = new Map<string, CompareTlc>()
  slots.forEach((slot) => {
    if (!slot.listingId) return
    byListingId.set(slot.listingId, {
      shippingFee: slot.shippingFee,
      totalLandedCost: slot.totalLandedCost,
      status: statusOf(slot),
    })
  })

  return { byListingId }
}
